import React, { useState, useMemo } from 'react';
import Modal from '@components/ui/Modal';
import { RiUserLine, RiSearchLine, RiLoader4Line } from '@remixicon/react';
import { useClients } from '../services/clientService';

/**
 * QuoteSearchByClientModal
 * Loads the broker's clients via React Query (cached for 5 minutes across
 * modal opens), then filters client-side by name / company / email.
 * On confirm calls onSelect(clientId, label).
 */
const QuoteSearchByClientModal = ({ isOpen, onClose, onSelect, brokerId = null }) => {
  const [query, setQuery] = useState('');
  // React Query caches the list for 5 minutes (see useClients), so the first
  // modal open fetches and every subsequent open within the window is instant.
  const { data: clients = [], isLoading: loading, error } = useClients(brokerId);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) =>
      (c.first_name || '').toLowerCase().includes(q) ||
      (c.last_name || '').toLowerCase().includes(q) ||
      (c.company_name || c.company || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q)
    );
  }, [clients, query]);

  const pickClient = (client) => {
    const id = client.client_id || client.id;
    if (!id) return;
    const name = [client.first_name, client.last_name].filter(Boolean).join(' ').trim();
    const label = name || client.email || 'Client';
    onSelect(id, label);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Search by Client" size="md">
      <div className="p-6 space-y-4">
        <div className="relative">
          <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, company, or email"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            autoFocus
          />
        </div>

        <div className="max-h-80 overflow-y-auto border border-gray-100 rounded-lg">
          {loading ? (
            <div className="p-4 flex items-center gap-2 text-sm text-gray-500">
              <RiLoader4Line className="w-4 h-4 animate-spin" /> Loading clients…
            </div>
          ) : error ? (
            <p className="p-4 text-sm text-red-600">{error?.message || 'Failed to load clients'}</p>
          ) : results.length === 0 ? (
            <p className="p-4 text-sm text-gray-500">
              {query ? 'No clients match your search.' : 'No clients found.'}
            </p>
          ) : (
            <ul>
              {results.map((client) => {
                const id = client.client_id || client.id || client.email;
                const company = client.company_name || client.company || '';
                return (
                  <li key={id}>
                    <button
                      type="button"
                      onClick={() => pickClient(client)}
                      className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                    >
                      <RiUserLine className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-gray-900 truncate">
                          {client.first_name} {client.last_name}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {company ? `${company} · ` : ''}{client.email || ''}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 rounded-full hover:bg-gray-100"
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default QuoteSearchByClientModal;
